import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Small data hook: runs `fn`, keeps the previous data while refetching,
 * optionally polls every `interval` seconds and can be re-run with `reload()`.
 */
export default function useApi(fn, deps = [], { interval = 0, enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const run = useCallback(async (silent) => {
    if (!silent) setState((s) => ({ ...s, loading: true }));
    try {
      const data = await fnRef.current();
      setState({ data, error: null, loading: false });
    } catch (e) {
      setState((s) => ({ data: s.data, error: e, loading: false }));
    }
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enabled) run(false); }, [enabled, run, ...deps]);
  useEffect(() => {
    if (!interval || !enabled) return undefined;
    const t = setInterval(() => run(true), interval * 1000);
    return () => clearInterval(t);
  }, [interval, enabled, run]);
  return { ...state, reload: () => run(true) };
}
